import { isValid, subYears } from 'date-fns';

/**
 * Utility functions for validation
 */

/**
 * Validates if a Date object represents a valid date for someone 18+ years old
 * @param date - Date object to validate
 * @returns boolean indicating if the date is valid and represents 18+ age
 */
export const isValidBirthdayDateObject = (date?: Date | null): boolean => {
  if (!date || !isValid(date)) return false;

  // Age validation (must be 18+)
  const today = new Date();
  const eighteenYearsAgo = subYears(today, 18);

  return date <= eighteenYearsAgo;
};

/**
 * Validates if a string is a valid email address
 * @param email - Email string to validate
 * @returns boolean indicating if the email is valid
 */
export const isValidEmail = (email: string): boolean => {
  // Enhanced email validation regex that's more strict
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && !email.includes('..');
};

/**
 * Validates if a string represents a valid phone number
 * @param phone - Phone string to validate
 * @returns boolean indicating if the phone is valid
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone.trim()) return false; // Phone is required by default

  // Remove all non-digit characters to check length and format
  const digitsOnly = phone.replace(/\D/g, '');

  // More comprehensive phone validation
  // US formats: (555) 555-5555, 555-555-5555, 555.555.5555, 5555555555
  // International: +1 555 555 5555, +44 20 7946 0958, etc.
  const phoneRegex =
    /^(?:\+?1[-.\s]?)?(?:\(?[0-9]{3}\)?[-.\s]?)?[0-9]{3}[-.\s]?[0-9]{4}$|^(?:\+[1-9]\d{0,3}[-.\s]?)?(?:\(?[0-9]{1,4}\)?[-.\s]?)?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,9}$/;

  return (
    phoneRegex.test(phone.trim()) &&
    digitsOnly.length >= 7 &&
    digitsOnly.length <= 15
  );
};

/**
 * Calculates account age in days from a creation date string
 * @param createdAt - ISO date string when account was created
 * @returns number of days since account creation, 0 if invalid date
 */
export const calculateAccountAge = (createdAt: string): number => {
  try {
    const created = new Date(createdAt);
    const now = new Date();

    // Handle invalid dates gracefully
    if (isNaN(created.getTime())) {
      return 0; // Return 0 days for invalid dates
    }

    const diffTime = Math.abs(now.getTime() - created.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};
